import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Image, TouchableOpacity } from 'react-native';
import { 
  Card, 
  Title, 
  Paragraph, 
  Button, 
  Surface,
  ActivityIndicator,
  useTheme,
  IconButton,
  Caption,
  Divider
} from 'react-native-paper';
import { ERDDAPService } from '../services/erddapService';
import { BoundingBox } from '../types/oceanographic';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { getConfigValue } from '../config/erddapConfig';
import { logger } from '../utils/logger';

interface DatasetOption {
  id: string;
  name: string;
  description: string;
  lastUpdate?: Date;
  available: boolean;
  dayOptions: {
    offset: number;
    date: Date | undefined;
    thumbnailUrl?: string;
    hasData: boolean;
    isLatest?: boolean;
  }[];
}

interface DatasetSelectorProps {
  bounds: BoundingBox;
  onSelectDatasets: (selections: {
    chlorophyll: { dataset: string; date: Date };
    sst: { dataset: string; date: Date };
  }) => void;
  onCancel: () => void;
}

const thumbnailSize = 80; // Smaller thumbnails for multiple days

export const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  bounds,
  onSelectDatasets,
  onCancel
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [selectedChlorophyll, setSelectedChlorophyll] = useState<{dataset: string; offset: number} | null>(null);
  const [selectedSST, setSelectedSST] = useState<{dataset: string; offset: number} | null>(null);
  const [chlorophyllOptions, setChlorophyllOptions] = useState<DatasetOption[]>([]);
  const [sstOptions, setSSTOptions] = useState<DatasetOption[]>([]);
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});
  
  // Date offsets to check - limited by configuration
  const previewDays = getConfigValue('PREVIEW_DAYS_TO_LOAD') as number;
  const dateOffsets = Array.from({ length: previewDays }, (_, i) => -i);

  useEffect(() => {
    loadDatasetPreviews().catch(err => logger.error('Failed to load dataset previews', err instanceof Error ? err : new Error(String(err))));
  }, [bounds]);

  const loadDatasetPreviews = async (): Promise<void> => {
    setLoading(true);
    
    // Set a timeout for the entire loading process
    const loadingTimeout = setTimeout(() => {
      logger.warn('Dataset loading timeout - using default selections');
      setLoading(false);
      // Set some defaults so the user can proceed
      if (chlorophyllOptions.length === 0) {
        setChlorophyllOptions([{
          id: 'default',
          name: 'VIIRS',
          description: 'VIIRS Chlorophyll-a Daily',
          available: true,
          dayOptions: [{ offset: 0, date: new Date(), thumbnailUrl: undefined, hasData: true, isLatest: true }]
        }]);
        setSelectedChlorophyll({ dataset: 'default', offset: 0 });
      }
      if (sstOptions.length === 0) {
        setSSTOptions([{
          id: 'default',
          name: 'Blended SST',
          description: 'NOAA Blended SST Daily',
          available: true,
          dayOptions: [{ offset: 0, date: new Date(), thumbnailUrl: undefined, hasData: true, isLatest: true }]
        }]);
        setSelectedSST({ dataset: 'default', offset: 0 });
      }
    }, 30000); // 30 second timeout
    
    try {
      // Chlorophyll datasets
      const chloroDatasets = [
        { id: 'default', name: 'VIIRS', description: 'VIIRS Chlorophyll-a Daily' },
        { id: 'gapFilled', name: 'VIIRS GAP-Filled', description: 'VIIRS Gap-Filled (DINEOF)' },
        { id: 's3a', name: 'Sentinel-3A', description: 'Sentinel-3A OLCI Daily' },
        { id: 's3b', name: 'Sentinel-3B', description: 'Sentinel-3B OLCI Daily' },
      ];

      // SST datasets
      const sstDatasets = [
        { id: 'default', name: 'Blended SST', description: 'NOAA Blended SST Daily' },
        { id: 'leo', name: 'LEO ACSPO', description: 'LEO ACSPO SST L3S NRT' },
      ];
      
      // Create batch loader
      const skipUnavailable = getConfigValue('SKIP_UNAVAILABLE_DATASETS') as boolean;
      
      // First, load timestamps for all datasets with timeout
      const timestampTimeout = 10000; // 10 seconds per request
      const chloroTimestamps = await Promise.allSettled(
        chloroDatasets.map(dataset => 
          Promise.race([
            ERDDAPService.fetchDatasetTimestamp('chlorophyll', dataset.id as 'default' | 'gapFilled' | 's3a' | 's3b'),
            new Promise<null>((_, reject) => setTimeout(() => reject(new Error('Timestamp fetch timeout')), timestampTimeout))
          ])
        )
      );
      
      
      const sstTimestamps = await Promise.allSettled(
        sstDatasets.map(dataset => 
          Promise.race([
            ERDDAPService.fetchDatasetTimestamp('sst', dataset.id as 'default' | 'leo'),
            new Promise<null>((_, reject) => setTimeout(() => reject(new Error('Timestamp fetch timeout')), timestampTimeout))
          ])
        )
      );

      // Process chlorophyll datasets
      const chloroOptions: DatasetOption[] = [];
      for (let i = 0; i < chloroDatasets.length; i++) {
        const dataset = chloroDatasets[i];
        const timestampResult = chloroTimestamps[i];
        
        if (timestampResult.status === 'rejected' && skipUnavailable) {
          continue;
        }
        
        const timestamp = timestampResult.status === 'fulfilled' ? timestampResult.value : null;
        const lastUpdate = timestamp !== null && timestamp !== undefined ? new Date(timestamp) : undefined;
        
        // Generate day options
        const dayOptions = dateOffsets.map((offset) => {
          let targetDate: Date | undefined;
          
          if (offset === 0) {
            // For latest, use undefined to get the most recent
            targetDate = undefined;
          } else if (lastUpdate) {
            // Calculate date based on the last available date
            targetDate = new Date(lastUpdate);
            targetDate.setDate(targetDate.getDate() + offset);
          } else {
            // Fallback if no lastUpdate
            targetDate = new Date(Date.now() + offset * 24 * 60 * 60 * 1000);
          }
          
          const url = ERDDAPService.getImageOverlayURL('chlorophyll', bounds, { 
            chlorophyllDataset: dataset.id as 'default' | 'gapFilled' | 's3a' | 's3b',
            targetDate 
          });
          
          // Store the exact date used for this dataset option
          // For offset 0 with lastUpdate available, use that timestamp
          // This ensures consistency between image and data fetch
          const dateForData = targetDate || (offset === 0 && lastUpdate ? lastUpdate : new Date());
          
          return {
            offset,
            date: dateForData,
            thumbnailUrl: url,
            hasData: true,
            isLatest: offset === 0
          };
        });
        
        chloroOptions.push({
          ...dataset,
          lastUpdate,
          available: timestamp !== null && timestamp !== undefined,
          dayOptions
        });
      }

      // Process SST datasets
      const sstOptions: DatasetOption[] = [];
      for (let i = 0; i < sstDatasets.length; i++) {
        const dataset = sstDatasets[i];
        const timestampResult = sstTimestamps[i];
        
        if (timestampResult.status === 'rejected' && skipUnavailable) {
          continue;
        }
        
        const timestamp = timestampResult.status === 'fulfilled' ? timestampResult.value : null;
        const lastUpdate = timestamp !== null && timestamp !== undefined ? new Date(timestamp) : undefined;
        
        // Generate day options - use static URLs to avoid overloading
        const dayOptions = dateOffsets.map((offset) => {
          let targetDate: Date | undefined;
          
          if (offset === 0) {
            // For latest, use undefined to get the most recent
            targetDate = undefined;
          } else if (lastUpdate) {
            // Calculate date based on the last available date
            targetDate = new Date(lastUpdate);
            targetDate.setDate(targetDate.getDate() + offset);
          } else {
            // Fallback if no lastUpdate
            targetDate = new Date(Date.now() + offset * 24 * 60 * 60 * 1000);
          }
          
          // Use static URL generation to avoid async calls
          const url = ERDDAPService.getImageOverlayURL('sst', bounds, { 
            sstDataset: dataset.id as 'default' | 'leo',
            targetDate 
          });
          
          // Store the exact date used for this dataset option
          const dateForData = targetDate || (offset === 0 && lastUpdate ? lastUpdate : new Date());
          
          return {
            offset,
            date: dateForData,
            thumbnailUrl: url,
            hasData: true,
            isLatest: offset === 0
          };
        });
        
        sstOptions.push({
          ...dataset,
          lastUpdate,
          available: timestamp !== null && timestamp !== undefined,
          dayOptions
        });
      }

      // Sort by most recent data first
      chloroOptions.sort((a, b) => {
        const dateA = a.lastUpdate?.getTime() ?? 0;
        const dateB = b.lastUpdate?.getTime() ?? 0;
        return dateB - dateA;
      });

      sstOptions.sort((a, b) => {
        const dateA = a.lastUpdate?.getTime() ?? 0;
        const dateB = b.lastUpdate?.getTime() ?? 0;
        return dateB - dateA;
      });

      setChlorophyllOptions(chloroOptions);
      setSSTOptions(sstOptions);

      // Auto-select the most recent dataset with data
      if (chloroOptions.length > 0 && chloroOptions[0].available) {
        setSelectedChlorophyll({ dataset: chloroOptions[0].id, offset: 0 });
      }
      if (sstOptions.length > 0 && sstOptions[0].available) {
        setSelectedSST({ dataset: sstOptions[0].id, offset: 0 });
      }
    } catch (error) {
      logger.error('Error loading dataset previews', error instanceof Error ? error : new Error(String(error)));
      // Set default options on error
      if (chlorophyllOptions.length === 0) {
        setChlorophyllOptions([{
          id: 'default',
          name: 'VIIRS',
          description: 'VIIRS Chlorophyll-a Daily',
          available: true,
          dayOptions: [{ offset: 0, date: new Date(), thumbnailUrl: undefined, hasData: true, isLatest: true }]
        }]);
        setSelectedChlorophyll({ dataset: 'default', offset: 0 });
      }
      if (sstOptions.length === 0) {
        setSSTOptions([{
          id: 'default',
          name: 'Blended SST',
          description: 'NOAA Blended SST Daily',
          available: true,
          dayOptions: [{ offset: 0, date: new Date(), thumbnailUrl: undefined, hasData: true, isLatest: true }]
        }]);
        setSelectedSST({ dataset: 'default', offset: 0 });
      }
    } finally {
      clearTimeout(loadingTimeout);
      setLoading(false);
    }
  };

  const handleConfirm = (): void => {
    if (!selectedChlorophyll || !selectedSST) return;

    const chloroDataset = chlorophyllOptions.find(d => d.id === selectedChlorophyll.dataset);
    const sstDataset = sstOptions.find(d => d.id === selectedSST.dataset);

    if (!chloroDataset || !sstDataset) return;

    const chloroDay = chloroDataset.dayOptions.find(d => d.offset === selectedChlorophyll.offset);
    const sstDay = sstDataset.dayOptions.find(d => d.offset === selectedSST.offset);

    if (!chloroDay || !sstDay) return;

    logger.info('DatasetSelector: Confirming selection', {
      chlorophyll: {
        dataset: selectedChlorophyll.dataset,
        date: chloroDay.date ? chloroDay.date.toISOString() : 'undefined (latest)',
        offset: selectedChlorophyll.offset
      },
      sst: {
        dataset: selectedSST.dataset,
        date: sstDay.date ? sstDay.date.toISOString() : 'undefined (latest)',
        offset: selectedSST.offset
      }
    });

    if (!chloroDay.date || !sstDay.date) {
      logger.warn('Dataset dates are not available');
      return;
    }

    onSelectDatasets({
      chlorophyll: { dataset: selectedChlorophyll.dataset, date: chloroDay.date },
      sst: { dataset: selectedSST.dataset, date: sstDay.date }
    });
  };

  const renderDatasetCard = (dataset: DatasetOption, type: 'chlorophyll' | 'sst'): React.JSX.Element => {
    const isSelected = type === 'chlorophyll' 
      ? selectedChlorophyll?.dataset === dataset.id
      : selectedSST?.dataset === dataset.id;
    
    const selectedOffset = type === 'chlorophyll'
      ? selectedChlorophyll?.offset
      : selectedSST?.offset;

    return (
      <Card style={[styles.datasetCard, !dataset.available && styles.unavailableCard]}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View style={styles.titleContainer}>
              <Title style={styles.datasetTitle}>{dataset.name}</Title>
              {dataset.lastUpdate && (
                <Caption style={styles.updateText}>
                  Latest: {dataset.lastUpdate.toLocaleDateString()}
                </Caption>
              )}
            </View>
          </View>
          
          <Caption numberOfLines={1} style={styles.description}>{dataset.description}</Caption>
          
          {dataset.available && (
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.thumbnailScroll}
            >
              {dataset.dayOptions.map((day) => {
                const key = `${dataset.id}-${day.offset}`;
                const isSelectedDay = isSelected && selectedOffset === day.offset;
                
                return (
                  <TouchableOpacity
                    key={key}
                    onPress={() => {
                      if (type === 'chlorophyll') {
                        setSelectedChlorophyll({ dataset: dataset.id, offset: day.offset });
                      } else {
                        setSelectedSST({ dataset: dataset.id, offset: day.offset });
                      }
                    }}
                    style={[
                      styles.dayOption,
                      isSelectedDay && styles.selectedDayOption
                    ]}
                  >
                    <View style={styles.thumbnailContainer}>
                      {day.thumbnailUrl !== null && day.thumbnailUrl !== undefined && day.thumbnailUrl !== '' ? (
                        <>
                          {!imageLoadStates[key] && (
                            <View style={[styles.thumbnail, styles.loadingContainer]}>
                              <ActivityIndicator size="small" />
                            </View>
                          )}
                          <Image 
                            source={{ uri: day.thumbnailUrl }}
                            style={[styles.thumbnail, !imageLoadStates[key] && { position: 'absolute' }]}
                            resizeMode="cover"
                            onLoad={() => setImageLoadStates(prev => ({ ...prev, [key]: true }))}
                            onError={(e) => {
                              logger.error(`Image load error for ${key}`, new Error(String(e.nativeEvent.error)));
                              setImageLoadStates(prev => ({ ...prev, [key]: true }));
                            }}
                          />
                        </>
                      ) : (
                        <View style={[styles.thumbnail, styles.noDataThumbnail]}>
                          <MaterialCommunityIcons 
                            name="image-off" 
                            size={24} 
                            color={theme.colors.onSurface} 
                          />
                        </View>
                      )}
                      {!day.hasData && (
                        <View style={styles.noDataOverlay}>
                          <Caption style={styles.noDataText}>No Data</Caption>
                        </View>
                      )}
                    </View>
                    <Caption style={styles.dayLabel}>
                      {day.offset === 0 ? 'Latest' : (day.date?.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) ?? 'N/A')}
                    </Caption>
                    {isSelectedDay && (
                      <MaterialCommunityIcons 
                        name="check-circle" 
                        size={20} 
                        color={theme.colors.primary}
                        style={styles.checkIcon}
                      />
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </Card.Content>
      </Card>
    );
  };


  return (
    <Surface style={styles.container}>
      <View style={styles.header}>
        <Title style={styles.title}>Select Data Sources</Title>
        <IconButton
          icon="close"
          size={24}
          onPress={onCancel}
        />
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <View style={styles.loadingContent}>
              <View style={styles.loadingIconContainer}>
                <MaterialCommunityIcons 
                  name="satellite-variant" 
                  size={64} 
                  color={theme.colors.primary} 
                  style={styles.loadingIcon}
                />
                <ActivityIndicator 
                  size="large" 
                  style={styles.loadingSpinner}
                  color={theme.colors.primary}
                />
              </View>
              <Title style={styles.loadingTitle}>Analyzing Ocean Data</Title>
              <Paragraph style={styles.loadingText}>
                Fetching latest satellite imagery from multiple sources...
              </Paragraph>
              <View style={styles.loadingSteps}>
                <View style={styles.loadingStep}>
                  <MaterialCommunityIcons name="check-circle" size={20} color={theme.colors.primary} />
                  <Caption style={styles.loadingStepText}>Connecting to NOAA satellites</Caption>
                </View>
                <View style={styles.loadingStep}>
                  <ActivityIndicator size="small" />
                  <Caption style={styles.loadingStepText}>Loading chlorophyll data</Caption>
                </View>
                <View style={styles.loadingStep}>
                  <MaterialCommunityIcons name="circle-outline" size={20} color={theme.colors.onSurfaceVariant} />
                  <Caption style={styles.loadingStepText}>Loading temperature data</Caption>
                </View>
              </View>
            </View>
          </View>
        ) : (
          <>
            {/* Chlorophyll Selection */}
            <View style={styles.section}>
              <Title style={styles.sectionTitle}>Chlorophyll Dataset</Title>
              <Caption style={styles.sectionCaption}>
                Select dataset and date with best coverage
              </Caption>
              {chlorophyllOptions.map((dataset) => (
                <View key={dataset.id} style={styles.datasetContainer}>
                  {renderDatasetCard(dataset, 'chlorophyll')}
                </View>
              ))}
            </View>

            <Divider style={styles.divider} />

            {/* SST Selection */}
            <View style={styles.section}>
              <Title style={styles.sectionTitle}>Sea Surface Temperature</Title>
              <Caption style={styles.sectionCaption}>
                Select dataset and date with best coverage
              </Caption>
              {sstOptions.map((dataset) => (
                <View key={dataset.id} style={styles.datasetContainer}>
                  {renderDatasetCard(dataset, 'sst')}
                </View>
              ))}
            </View>
          </>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <Button
          mode="outlined"
          onPress={onCancel}
          style={styles.footerButton}
        >
          Cancel
        </Button>
        <Button
          mode="contained"
          onPress={handleConfirm}
          style={styles.footerButton}
          disabled={loading || !selectedChlorophyll || !selectedSST}
        >
          Confirm Selection
        </Button>
      </View>
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 48,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  section: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 4,
  },
  sectionCaption: {
    marginBottom: 12,
    opacity: 0.7,
  },
  datasetContainer: {
    marginBottom: 12,
  },
  datasetCard: {
    elevation: 2,
  },
  unavailableCard: {
    opacity: 0.6,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleContainer: {
    flex: 1,
  },
  datasetTitle: {
    fontSize: 16,
    marginBottom: 2,
  },
  updateText: {
    fontSize: 12,
  },
  description: {
    marginBottom: 12,
  },
  thumbnailScroll: {
    marginTop: 8,
  },
  dayOption: {
    marginRight: 12,
    alignItems: 'center',
  },
  selectedDayOption: {
    transform: [{ scale: 1.05 }],
  },
  thumbnailContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    elevation: 2,
  },
  thumbnail: {
    width: thumbnailSize,
    height: thumbnailSize,
    backgroundColor: '#f0f0f0',
  },
  loadingContent: {
    alignItems: 'center',
    maxWidth: 300,
  },
  loadingIconContainer: {
    position: 'relative',
    marginBottom: 24,
  },
  loadingIcon: {
    opacity: 0.9,
  },
  loadingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  loadingTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  loadingText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    opacity: 0.7,
    paddingHorizontal: 16,
  },
  loadingSteps: {
    width: '100%',
  },
  loadingStep: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingHorizontal: 16,
  },
  loadingStepText: {
    marginLeft: 12,
    fontSize: 14,
  },
  noDataThumbnail: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  noDataOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    color: 'white',
    fontSize: 10,
  },
  dayLabel: {
    marginTop: 4,
    fontSize: 11,
  },
  checkIcon: {
    position: 'absolute',
    top: -4,
    right: -4,
  },
  divider: {
    marginVertical: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  footerButton: {
    flex: 1,
    marginHorizontal: 8,
  },
});